<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"

// Stores
import { useSourceStore } from "@core/stores/source.store"

// Components - Onglets du compteur
import MeterCardTechnicalData from "@core/components/source/meter/display/card/MeterCardTechnicalData.vue"
import MeterCardConsent from "@core/components/source/meter/display/card/MeterCardConsent.vue"
import MeterCardOffer from "@core/components/source/meter/display/card/MeterCardOffer.vue"
import MeterCardReceivedData from "@core/components/source/meter/display/card/MeterCardReceivedData.vue"
import MeterCardNotes from "@core/components/source/meter/display/card/MeterCardNotes.vue"
import { METER_TYPES_LIST, SOURCES_TYPES } from "@core/constants"
import {
    InformationCpcu,
    InformationDalkia,
    InformationPCE,
    InformationPDL,
    InformationRegaz,
    InformationElecSavoie,
    InformationEauBordeaux,
} from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            technicalData: "Données contractuelles et techniques",
            pricingAndSupplier: "Tarification et fournisseur",
            consent: "Consentement",
            receivedData: "Données reçues",
            notes: "Notes",
            offerdetails: "Détails de l'offre",
            mandats: "Mandats",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Props du composant MeterCardTab
 */
type Props = {
    /** Identifiant unique du compteur */
    meterId: string
}

/**
 * Types de compteurs supportés pour l'affichage des onglets
 */
type MeterType = "pce" | "pdl" | "cpcu" | "dalkia" | "eaubordeaux"

/**
 * Nom des composants disponibles pour les onglets
 */
type ComponentName = keyof typeof componentsMap

/**
 * Interface pour un onglet de compteur
 */
interface MeterTab {
    index?: number
    /** Titre affiché dans l'onglet */
    title: string
    /** Nom du composant à afficher (optionnel) */
    componentName?: ComponentName
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// REACTIVE STATE
// ================================

const sourceStore = useSourceStore()
/**
 * Mapping des composants disponibles pour les onglets
 */
const componentsMap = {
    MeterCardTechnicalData,
    MeterCardConsent,
    MeterCardOffer,
    MeterCardReceivedData,
    MeterCardNotes,
} as const

// ================================
// COMPUTED PROPERTIES
// ================================
/**
 * Récupère les informations de la source par son ID
 * @returns La source correspondant au meterId ou undefined
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Récupère les informations spécifiques au type de la source (pdl, pce, cpcu, regaz, elecsavoie, dalkia, eaubordeaux)
 */
const getInformationByType = computed(() => {
    return sourceStore.getInformationByType(props.meterId)
})

/**
 * Vérifie si le compteur a des erreurs de consentement ou droit d'accès GRDF
 * @returns true si le consentement n'est pas actif ou droit d'accès GRDF inactif
 */
const hasConsentOrDdaError = computed((): boolean => {
    const source = getSourceById.value
    if (getInformationByType?.value?.consentement?.etat !== "active") {
        return true
    }
    if (
        source?.type === SOURCES_TYPES.PCE &&
        source?.informations?.pce?.droit_access?.etat !== "Active"
    ) {
        return true
    }
    return false
})

/**
 * Vérifie si le compteur a des erreurs majeures (inexistant)
 * @returns true si le compteur est inexistant
 */
const hasMajorError = computed((): boolean => {
    const source = getSourceById.value
    if (
        source?.type === SOURCES_TYPES.PDL &&
        source?.informations?.pdl?.etat === "inexistant"
    ) {
        return true
    }
    return false
})

const hasDdaActiveButNoData = computed((): boolean => {
    const source = getSourceById.value
    // Vérifie si le droit d'accès GRDF est actif mais la fréquence est nulle
    // cela indique que le compteur est actif mais aucune donnée n'est reçue
    // on n'a pas de données contractuelles : il faut lancer un update des données
    if (
        source?.type === SOURCES_TYPES.PCE &&
        source?.informations?.pce?.droit_access?.etat === "Active" &&
        source?.informations?.pce?.compteur.frequence === null
    ) {
        return true
    }
    return false
})

/**
 * Vérifie si au moins une donnée est incomplète pour le compteur actuel
 * @returns true si au moins une donnée n'est pas complète
 */
const hasIncompleteData = computed((): boolean => {
    const source = getSourceById.value
    if (!source?.informations) return false

    const info = source.informations as
        | InformationPCE
        | InformationPDL
        | InformationCpcu
        | InformationRegaz
        | InformationDalkia
        | InformationElecSavoie
        | InformationEauBordeaux

    // Vérifie si au moins une variable n'a pas de date_from
    const variables = info?.data?._variables || []

    return variables.some((variable) => {
        const row = variable as Record<
            string,
            { date_from?: Date | string | null } | undefined | null
        >
        const frequencies = ["raw", "hourly", "daily"] as const
        return frequencies.some((freq) => {
            const data = row[freq]
            return data !== undefined && data !== null && !data.date_from
        })
    })
})

/**
 * Onglets par défaut communs à tous les types de compteurs
 * @returns Liste des onglets par défaut
 */
const DEFAULT_TABS = [
    {
        title: t("technicalData"),
        componentName: "MeterCardTechnicalData",
    },
    {
        title: t("receivedData"),
        componentName: "MeterCardReceivedData",
    },
    {
        title: t("notes"),
        componentName: "MeterCardNotes",
    },
] as MeterTab[]

/**
 * Onglets spécifiques selon le type de compteur
 * @returns Configuration des onglets par type de compteur
 */
const TABS_BY_METER_TYPE = {
    [SOURCES_TYPES.PCE]: [
        {
            title: t("pricingAndSupplier"),
            componentName: "MeterCardOffer",
        },
        {
            title: t("consent"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.PDL]: [
        {
            title: t("pricingAndSupplier"),
            componentName: "MeterCardOffer",
        },
        {
            title: t("consent"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.CPCU]: [
        {
            title: t("mandats"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.DALKIA]: [
        {
            title: t("mandats"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.EAU_BORDEAUX]: [
        {
            title: t("mandats"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.REGAZ]: [
        {
            title: t("mandats"),
            componentName: "MeterCardConsent",
        },
    ],
    [SOURCES_TYPES.ELECSAVOIE]: [
        {
            title: t("mandats"),
            componentName: "MeterCardConsent",
        },
    ],
} as Record<MeterType, MeterTab[]>

/**
 * Liste complète des onglets selon le type de compteur
 * Combine les onglets par défaut avec ceux spécifiques au type
 * @returns Liste des onglets triés par valeur ou liste vide si type non supporté
 */
const getTabsBySourceId = computed((): MeterTab[] => {
    const meterType = getSourceById.value?.type as MeterType | undefined

    // Si pas de type ou type non autorisé, aucun onglet
    if (!meterType || !METER_TYPES_LIST.includes(meterType)) {
        return []
    }

    const DEFAULT_ORDERS_TABS = [
        "MeterCardTechnicalData",
        "MeterCardOffer",
        "MeterCardConsent",
        "MeterCardReceivedData",
        "MeterCardNotes",
    ]

    // Fusion et tri des onglets par défaut et spécifiques
    const allTabs = [...DEFAULT_TABS, ...(TABS_BY_METER_TYPE[meterType] || [])]

    const ORDER_TABS = [] as MeterTab[]
    DEFAULT_ORDERS_TABS.forEach((component) => {
        const tabFound = allTabs.find((tab) => tab.componentName === component)
        // Si le composant de l'onglet est défini, on enregistre son index pour le tri
        if (tabFound) {
            ORDER_TABS.push(tabFound)
        }
    })

    return ORDER_TABS
})

const currentTabIndex = ref<number>(0)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <div class="meter-card-tab">
        <!-- ================================ -->
        <!-- CONTENEUR DES ONGLETS -->
        <!-- ================================ -->
        <BaseTabs
            :value="currentTabIndex"
            @update:value="currentTabIndex = $event"
        >
            <BaseTabList
                :pt="{
                    tabList: 'flex flex-row',
                }"
            >
                <BaseTab
                    class="flex flex-row"
                    v-for="(tab, index) in getTabsBySourceId"
                    :value="index"
                    :key="index"
                    :pt="{
                        root: 'flex-1 justify-center',
                    }"
                    >{{ tab.title }}
                    <BaseIcon
                        v-if="
                            tab.componentName === 'MeterCardReceivedData' &&
                            (hasIncompleteData ||
                                hasMajorError ||
                                hasDdaActiveButNoData)
                        "
                        icon="pi pi-exclamation-triangle"
                        class="ml-2"
                        style="color: orange"
                    />
                    <BaseIcon
                        v-if="
                            tab.componentName === 'MeterCardConsent' &&
                            hasConsentOrDdaError
                        "
                        icon="pi pi-exclamation-triangle"
                        class="ml-2"
                        style="color: orange"
                    />
                </BaseTab>
            </BaseTabList>
            <BaseTabPanels
                :pt="{
                    root: 'md:p-4! p-0!',
                }"
            >
                <!-- ================================ -->
                <!-- PANNEAU ONGLET DYNAMIQUE -->
                <!-- ================================ -->
                <BaseTabPanel
                    v-for="(tab, index) in getTabsBySourceId"
                    :key="index"
                    :value="index"
                >
                    <!-- Composant dynamique selon le type de compteur -->
                    <component
                        :is="componentsMap[tab.componentName]"
                        v-if="tab.componentName && currentTabIndex === index"
                        :meter-id="meterId"
                    />
                </BaseTabPanel>
            </BaseTabPanels>
        </BaseTabs>
    </div>
</template>

<style scoped>
/* ================================ */
/* CONTENEUR DES ONGLETS */
/* ================================ */
:deep(.p-tabview-tablist-item-active span) {
    color: var(--p-blue-700);
}
</style>
